/**
 * track() do Olho de Hórus (docs/SOLUCAO-TECNICA.md §7.4, docs/SITE.md §9, RF-OBS-01).
 *
 * - Sem `endpoint`, é no-op: nada sai do navegador (site v1, antes do site-api).
 * - Fila em memória, envio em lote a cada `flushIntervalMs` e quando a página some
 *   (visibilitychange/pagehide), via navigator.sendBeacon.
 * - Regra de ouro 1 do AGENTS.md: eventos levam só ids, rota e data-track. Props aceitam apenas
 *   valores curtos e primitivos; strings com cara de CPF, e-mail ou telefone são descartadas.
 */

import { hasPii } from "./pii";

export type PropValue = string | number | boolean;
export type Props = Record<string, PropValue | undefined>;

export type ObsEvent = {
  event: string;
  path: string;
  ts: number;
  release: string;
  viewport: string;
  props: Record<string, PropValue>;
};

export type Transport = (url: string, body: string) => boolean;

export type TrackerOptions = {
  /** URL do coletor (ex.: "/api/events"). Ausente = no-op. */
  endpoint?: string | undefined;
  /** Hash do commit do build. */
  release?: string | undefined;
  flushIntervalMs?: number;
  /** Para testes: substitui navigator.sendBeacon. */
  transport?: Transport;
};

export type Tracker = {
  readonly enabled: boolean;
  track: (event: string, props?: Props) => void;
  flush: () => void;
};

const EVENT_NAME = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/;
const PROP_KEY = /^[a-z][a-zA-Z0-9_]{0,39}$/;
const MAX_STRING = 64;
const MAX_QUEUE = 50;

export function sanitizeProps(props: Props = {}): Record<string, PropValue> {
  const out: Record<string, PropValue> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || !PROP_KEY.test(key)) continue;
    if (typeof value === "number" && !Number.isFinite(value)) continue;
    if (typeof value === "string") {
      if (value.length > MAX_STRING || hasPii(value)) continue;
    }
    out[key] = value;
  }
  return out;
}

const beacon: Transport = (url, body) =>
  typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
    ? navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))
    : false;

export function createTracker(options: TrackerOptions = {}): Tracker {
  const { endpoint, release = "dev", flushIntervalMs = 30_000, transport = beacon } = options;

  if (!endpoint) {
    return { enabled: false, track: () => {}, flush: () => {} };
  }

  let queue: ObsEvent[] = [];

  const flush = () => {
    if (queue.length === 0) return;
    const batch = queue;
    queue = [];
    transport(endpoint, JSON.stringify({ events: batch }));
  };

  const track = (event: string, props?: Props) => {
    if (!EVENT_NAME.test(event)) return;
    queue.push({
      event,
      // Só a rota: nunca query string (pode carregar dado de campanha ou pessoal).
      path: location.pathname,
      ts: Date.now(),
      release,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      props: sanitizeProps(props),
    });
    if (queue.length >= MAX_QUEUE) flush();
  };

  setInterval(flush, flushIntervalMs);
  addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });

  return { enabled: true, track, flush };
}
